import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { Channel, getProgramTitle, useChannelLiveProgram } from '@kaltura-ott/tvpil-shared';
import { getAssetEpisodeIndication } from '@kaltura-ott/tvpil-shared/lib/rhino/data/asset';

import { useMouseEnterHandle } from 'components/presenters/Card/hooks/useMouseEnterHandle/useMouseEnterHandle';
import { useMouseLeaveHandle } from 'components/presenters/Card/hooks/useMouseLeaveHandle/useMouseLeaveHandle';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { getSeasonEpisodeTitle } from 'components/widgets/Rail/utils';
import { WidgetProviderSettings } from 'context/widgetProvider/types';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useParentalPinStore, useRecordRepository } from 'hooks';
import routes from 'routesPaths';
import { getAriaLabelForAsset } from 'utils';

import { useClickHandler } from '../useClickHandler/useClickHandler';
import { useHandleClickInfo } from '../useHandleClickInfo';
import { useHeroCardImages } from '../useHeroCardImages';
import { useHeroCardRef } from '../useHeroCardRef';
import { useUpdateMaxiPlayer } from '../useUpdateMaxiPLayer/useUpdateMaxiPlayer';

interface Props {
  channel: Channel;
  itemWidth: number;
  imageHeight: number;
  timestamp: number;
  index: number;
}

function useHeroCard({ channel, itemWidth, imageHeight, timestamp, index }: Props) {
  const link = routes.live.link(channel.id, 'program');
  // using version v2 of the rail provider
  const { onHover, nextButtons } = useRailContext();
  // using widget provider
  const { settings, imageWidth,  isGrid } = useWidgetContext<Channel, WidgetProviderSettings>();
  // getting live program for the channel
  const { liveProgram } = useChannelLiveProgram({ channelId: channel.id, timestamp });
  // adults
  const isAdult = !!(liveProgram?.isAdult ?? channel.isAdult);
  // channel has no program
  const isChannelHasNoProgram = liveProgram ? Boolean(liveProgram.isNotFound) : true;
  // refs
  const handleLinkRef = useRef(null);

  // temp solution
  useUpdateMaxiPlayer({ startDate: liveProgram?.startDate, channelId: liveProgram?.channelId, index });

  const { ref, handleRef } = useHeroCardRef(itemWidth, imageHeight);
  // handlers
  const onMouseEnter = useMouseEnterHandle({ ref, itemWidth, imageHeight, isGrid, onHover, nextButtons });
  const onMouseLeave = useMouseLeaveHandle(onHover);

  // Need to think about this
  const parentalPinStore = useParentalPinStore();
  const isAdultPinNotEntered = !parentalPinStore.isAdultPinEntered;
  const isRestricted = !!(isAdult && isAdultPinNotEntered);
  // 

  const clickHandler = useClickHandler({
    channelId: channel.id,
    isChannelHasNoProgram,
    data: liveProgram ?? channel,
    link,
    mediaTypeParam: 'program',
    imageHeight,
    isPurchasable: !channel.isEntitled,
    collectionType: 'hero',
    mosaicExternalId: liveProgram?.mosaicExternalId,
    isRestricted,
    parentalPinStore
  });
  const { handleClickInfo } = useHandleClickInfo({
    channelId: channel.id,
    id: liveProgram?.id!,
    externalId: liveProgram?.mosaicExternalId,
    mediaTypeParam: 'program',
    isChannelHasNoProgram,
  });
  // images urls
  const { backgroundImageUrl, defaultImageUrl, channelLogoUrl } = useHeroCardImages({
    channel,
    imageType: settings.imageType,
    imageWidth,
    liveProgram,
    channelIconType: settings.channelIconType,
    channelIconRatio: settings.channelIconRatio!,
    imageHeight,
    isChannelHasNoProgram,
  });
  const imageUrl = isChannelHasNoProgram ? channelLogoUrl : backgroundImageUrl;
  // translations and titles
  const intl = useIntl();
  const title = liveProgram?.title ? getProgramTitle(liveProgram) : channel.title;
  const seasonInfoData = liveProgram && getAssetEpisodeIndication(liveProgram);
  const seasonEpisodeTitle = seasonInfoData ? getSeasonEpisodeTitle(intl, seasonInfoData, ':', true) : '';

  // For record indication
  const recordRepository = useRecordRepository();
  const record = liveProgram?.id ? recordRepository.records.get(liveProgram.id.toString()) : null;
  const seriesRecord = liveProgram?.seriesId ? recordRepository.seriesRecords.get(liveProgram.seriesId) : null;
  const isRecord = Boolean(record || seriesRecord);
  const isSeriesRecord = Boolean(seriesRecord);
  //

  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title,
    channelName: channel.title,
    placeDescription: 'hero',
    description: seasonEpisodeTitle,
    isRestricted,
    isPurchaseContent: !channel.isEntitled,
    isRecord,
    isSeriesRecord,
  });

  return {
    link,
    title,
    isAdult,
    isRecord,
    imageUrl,
    ariaLabel,
    handleRef,
    liveProgram,
    clickHandler,
    onMouseEnter,
    onMouseLeave,
    handleLinkRef,
    isSeriesRecord,
    channelLogoUrl,
    defaultImageUrl,
    handleClickInfo,
    seasonEpisodeTitle,
    isChannelHasNoProgram,
  };
}

export { useHeroCard };
